import {ChangeDetectionStrategy, Component, inject, Input} from '@angular/core';
import {createSelector, Store} from '@ngrx/store';
import {SelectPipe} from '@aviary/pipes/select.pipe';
import {ExpandableCardComponent} from '@aviary/components/expandable-card/expandable-card.component';
import {MatExpansionPanelState} from '@angular/material/expansion';
import {ExpandableCardToggleButtonComponent} from '@aviary/components/expandable-card/expandable-card-toggle-button/expandable-card-toggle-button.component';
import {CatalogSearchFilterOptionChangedEvent, CatalogSearchFilterQueryParamName, CatalogSearchFilterSelectOptions} from '@store/catalog/catalog.types';
import {
  buildSelectedValuesFrom,
  createFilterParamUpdateActionFor,
  createFilterQueryParamNameFrom,
  filterOptionsWithSelectedFirstFor
} from '@store/catalog/catalog.helpers';
import {CatalogFilterOptionsComponent} from '@app/catalog/search/catalog-filters/catalog-filter-options/catalog-filter-options.component';
import {RouterSelectors} from '@store/aviary/aviary.adapter';
import {snapshot} from '@aviary/store/store.helpers';
import {MxSelector} from '@aviary/store/store.types';
import {ActionButtonsComponent} from '@aviary/components/action-buttons/action-buttons.component';
import * as CommonSelectors from '@aviary/store/common/common.selectors';
import {isPopulatedArray} from '@aviary/store/common/typing.helpers';
import {CatalogActions} from '@store/catalog/catalog.actions';
import {TextButtonComponent} from '@aviary/buttons/text-button/text-button.component';
import {ViewportSelectors} from '@aviary/store/viewport/viewport.selectors';

const MAX_VISIBLE_OPTIONS: number = 8;

@Component({
  selector: 'px-catalog-filter-card',
  host: {
    class: 'empty:hidden' // Hide this component if it doesn't render anything.  This prevents extra vertical gaps around cards.
  },
  templateUrl: './catalog-filter-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SelectPipe, ExpandableCardComponent, ExpandableCardToggleButtonComponent, CatalogFilterOptionsComponent, ActionButtonsComponent, TextButtonComponent
  ]
})
export class CatalogFilterCardComponent {
  @Input({required: true})
  public set filterName(filterName: string) {
    this._filterName = filterName;
    this.queryParamName = createFilterQueryParamNameFrom(filterName);

    const allOptions: MxSelector<CatalogSearchFilterSelectOptions> = filterOptionsWithSelectedFirstFor(filterName);

    this.visibleOptions = createSelector(
      allOptions,
      options => options.slice(0, MAX_VISIBLE_OPTIONS)
    );

    this.visibleOptionsExist = createSelector(
      this.visibleOptions,
      isPopulatedArray
    );

    this.moreOptionsExist = createSelector(
      allOptions,
      options => options.length > MAX_VISIBLE_OPTIONS
    );
  }

  public get filterName(): string {
    return this._filterName;
  }

  @Input()
  public set expansionState(state: MatExpansionPanelState) {
    this._expansionState = state;
  }

  public get expansionState(): MatExpansionPanelState {
    return this._expansionState;
  }


  @Input({required: true}) public cardTitle: string;

  public readonly viewportSelectors = ViewportSelectors;
  public _expansionState: MatExpansionPanelState = 'collapsed';
  public visibleOptions: MxSelector<CatalogSearchFilterSelectOptions> = createSelector(() => [] as CatalogSearchFilterSelectOptions);
  public visibleOptionsExist: MxSelector<boolean> = CommonSelectors.alwaysFalse;
  public moreOptionsExist: MxSelector<boolean> = CommonSelectors.alwaysFalse;

  private readonly store: Store = inject(Store);
  private _filterName: string;
  private queryParamName: CatalogSearchFilterQueryParamName;

  public onExpansionStateChanged(newExpansionState: MatExpansionPanelState): void {
    this._expansionState = newExpansionState;
  }

  public onSelectionChanged(event: CatalogSearchFilterOptionChangedEvent): void {
    const selectedValues: Set<string> = buildSelectedValuesFrom(snapshot(RouterSelectors.queryParamValue(this.queryParamName), this.store));

    if (event.selected) {
      selectedValues.add(event.value);
    } else {
      selectedValues.delete(event.value);
    }

    this.store.dispatch(createFilterParamUpdateActionFor(this.queryParamName, [...selectedValues]));
  }

  public onClickSeeAllButton(): void {
    this.store.dispatch(CatalogActions.showAllFilterOptions(this.filterName, this.cardTitle))
  }
}
